/**
 * Синхронизация анкеты «Помощь ИИ» с полями заказа в OrderEdit.
 */

const PICKUP_CODES = new Set(['self-delivery', 'kjivjv9u39oijskj']);
const DELIVERY_CODES = new Set([
  'courier',
  'vozimby',
  'euro987fh',
  'marshrutkapolock',
  'redirect',
]);

export function parseCapacityAh(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return [];
  const numbers = s
    .replace(/ач|а\.?ч\.?|ah/gi, ' ')
    .split(/[+,\s/;]+/)
    .map((x) => parseFloat(x.trim()))
    .filter((n) => !Number.isNaN(n) && n > 0);
  return numbers;
}

export function totalCapacityAh(raw) {
  const nums = parseCapacityAh(raw);
  if (nums.length) return nums.reduce((a, b) => a + b, 0);
  if (String(raw || '').trim()) return 60;
  return 0;
}

export function formatCapacityLabel(raw) {
  const nums = parseCapacityAh(raw);
  if (nums.length) return nums.map((n) => `${n} А·ч`).join(' + ');
  if (String(raw || '').trim()) return '60 А·ч';
  return '';
}

export function mapDeliveryToFulfillment(code) {
  const c = String(code || '').trim();
  if (!c) return 'unknown';
  if (PICKUP_CODES.has(c)) return 'pickup';
  if (DELIVERY_CODES.has(c)) return 'delivery';
  return 'unknown';
}

export function detectAgmEfbFromFuel(fuelType) {
  const f = String(fuelType || '').toLowerCase();
  return /agm|efb|агм|ефб/.test(f);
}

export function parsePriceNumber(price) {
  if (price == null || price === '') return null;
  const n = parseFloat(String(price).replace(/[^\d.,]/g, '').replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

function batteryTypeTags(item) {
  if (item.battery_types && item.battery_types.length) return item.battery_types;
  const blob = `${item.pricecategory || ''} ${item.name || ''}`.toUpperCase();
  const tags = [];
  if (blob.includes('AGM') || blob.includes('АГМ')) tags.push('AGM');
  if (blob.includes('EFB') || blob.includes('ЕФБ')) tags.push('EFB');
  return tags;
}

/** Гарантия (мес.) — Bitrix WARRANTY, не COUNT_DAY (дни на складе). */
export function formatWarrantyMonths(it) {
  const raw = it.warranty_months != null && it.warranty_months !== ''
    ? it.warranty_months
    : (it.warranty != null && it.warranty !== '' ? it.warranty : '');
  const s = String(raw).trim();
  if (!s || s === '0') return '';
  const n = parseInt(s, 10);
  if (!Number.isNaN(n) && n > 0) return String(n);
  return s;
}

export function parseWarehouseDays(countday) {
  if (countday == null || countday === '') return null;
  const n = parseInt(String(countday).trim(), 10);
  return Number.isNaN(n) || n <= 0 ? null : n;
}

/** Единый формат строки каталога для таблицы в модалке ИИ */
export function normalizeCatalogItem(raw) {
  const it = raw || {};
  const stock = it.stock_total != null ? it.stock_total : it.count;
  return {
    name: it.name || '',
    href: it.href || it.link || '',
    price: it.price,
    korpus: it.korpus || it.corpus || '',
    brend: it.brend || '',
    capacity: it.capacity || it.emkost,
    emkost: it.emkost || it.capacity,
    stock_total: stock != null && stock !== '' ? Number(stock) || 0 : null,
    bonus: it.bonus != null ? it.bonus : it.seba,
    battery_types: batteryTypeTags(it),
    pricecategory: it.pricecategory || '',
    country: it.country || '',
    warranty_months: formatWarrantyMonths(it),
    warehouse_days: parseWarehouseDays(
      it.warehouse_days != null && it.warehouse_days !== '' ? it.warehouse_days : it.countday,
    ),
    starttok: it.starttok,
    polarka: it.polarka,
    artikul: it.artikul,
    length: it.length,
    width: it.width,
    height: it.height,
    skladm: Array.isArray(it.skladm) ? it.skladm : [],
  };
}

export function normalizeCatalogItems(items) {
  return (items || []).map(normalizeCatalogItem);
}

export function calcCatalogPriceBounds(items) {
  const prices = (items || [])
    .map((it) => parsePriceNumber(it.price))
    .filter((p) => p != null && p > 0);
  if (!prices.length) return { min: null, max: null };
  return {
    min: Math.floor(Math.min(...prices)),
    max: Math.ceil(Math.max(...prices)),
  };
}

/**
 * @param {Object} orderContext
 * @param {Object} form — mutable form state in modal
 */
export function applyOrderContextToForm(orderContext, form) {
  const ctx = orderContext || {};
  const capacityRaw = ctx.batteryCapacityNew;
  const hasCapacity = String(capacityRaw || '').trim().length > 0;
  const tradeAmount = parsePriceNumber(ctx.tradeInAmountNew);

  if (hasCapacity) {
    form.tradeIn = 'yes';
    form.tradeInCapacityAh = totalCapacityAh(capacityRaw);
    form.tradeInCapacityLabel = formatCapacityLabel(capacityRaw);
    form.tradeInAmountRub = tradeAmount != null ? tradeAmount : null;
  }

  const dt = ctx.deliveryTypes;
  if (dt && (dt.value || dt.label)) {
    form.deliveryTypeCode = dt.value || '';
    form.deliveryTypeLabel = dt.label || dt.value || '';
    form.fulfillment = mapDeliveryToFulfillment(dt.value);
  }

  if (ctx.fuelType && detectAgmEfbFromFuel(ctx.fuelType)) {
    form.agmEfb = true;
  }
}

export function buildQuestionnairePayload(form, options = {}) {
  const includePrice = options.includePrice !== false;
  const q = {
    trade_in_similar_capacity: form.tradeIn,
    city: (form.city || '').trim(),
    fulfillment: form.fulfillment,
    agm_efb: !!form.agmEfb,
  };
  if (options.corpusBounds) {
    const cb = options.corpusBounds;
    if (cb.min != null) q.corpus_price_min = Number(cb.min);
    if (cb.max != null) q.corpus_price_max = Number(cb.max);
  }
  if (includePrice) {
    q.price_min = form.priceMin != null && form.priceMin !== '' ? Number(form.priceMin) : null;
    q.price_max = form.priceMax != null && form.priceMax !== '' ? Number(form.priceMax) : null;
  }
  if (form.deliveryTypeCode) q.delivery_type_code = form.deliveryTypeCode;
  if (form.deliveryTypeLabel) q.delivery_type_label = form.deliveryTypeLabel;
  if (form.tradeIn === 'yes') {
    if (form.tradeInCapacityAh) q.trade_in_capacity_ah = form.tradeInCapacityAh;
    if (form.tradeInAmountRub != null) q.trade_in_amount_rub = form.tradeInAmountRub;
  }
  return q;
}
